export const RESUME_MAX_AGE_DAYS = 180;

const KEY_PREFIX = 'onDemandResume:';
// Scoped by participantId when the viewer is identified (logged in), so a second account on a
// shared device — a kiosk, a shared meeting-room laptop — never inherits the first account's
// viewing position. Without it, falls back to the plain per-webinar key: the only option for an
// anonymous viewer, who has no participantId to scope by in the first place.
const key = (webinarId: string, participantId?: string) =>
    participantId ? `${KEY_PREFIX}${webinarId}:${participantId}` : `${KEY_PREFIX}${webinarId}`;

export interface ResumeRecord {
    position: number;
    completed: boolean;
    /** epoch milliseconds */
    updatedAt: number;
}

function isRecord(value: unknown): value is ResumeRecord {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as ResumeRecord).position === 'number' &&
        typeof (value as ResumeRecord).completed === 'boolean' &&
        typeof (value as ResumeRecord).updatedAt === 'number'
    );
}

export function readResume(webinarId: string, participantId?: string): ResumeRecord | null {
    try {
        const raw = globalThis.localStorage?.getItem(key(webinarId, participantId));
        if (!raw) return null;

        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed)) return null;

        if (Date.now() - parsed.updatedAt > RESUME_MAX_AGE_DAYS * 86_400_000) {
            try {
                globalThis.localStorage?.removeItem(key(webinarId, participantId));
            } catch {
                /* ignore */
            }
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

export function writeResume(webinarId: string, position: number, completed: boolean, participantId?: string): void {
    try {
        const record: ResumeRecord = {
            position: Math.max(0, Math.floor(position)),
            completed,
            updatedAt: Date.now()
        };
        globalThis.localStorage?.setItem(key(webinarId, participantId), JSON.stringify(record));
    } catch {
        /* ignore */
    }
}

export function markCompleted(webinarId: string, participantId?: string): void {
    const existing = readResume(webinarId, participantId);
    writeResume(webinarId, existing?.position ?? 0, true, participantId);
}
